import { Injectable, PLATFORM_ID, Renderer2, RendererFactory2, inject } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

export type NotdTheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'notd-theme';
const DEFAULT_THEME: NotdTheme = 'light';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private renderer: Renderer2;

  /** Observable stream of the current theme. Components can subscribe for live updates. */
  public currentTheme$ = new BehaviorSubject<NotdTheme>(DEFAULT_THEME);

  constructor() {
    // Renderer2 cannot be injected directly into a service — use RendererFactory2
    const rendererFactory = inject(RendererFactory2);
    this.renderer = rendererFactory.createRenderer(null, null);

    // Load persisted theme on service creation (browser only)
    const saved = this.loadTheme();
    this.applyTheme(saved);
  }

  /** Get the current active theme. */
  get currentTheme(): NotdTheme {
    return this.currentTheme$.value;
  }

  /** Set and persist a new theme. */
  setTheme(theme: NotdTheme): void {
    this.saveTheme(theme);
    this.applyTheme(theme);
  }

  /** Read the persisted theme from localStorage. Falls back to DEFAULT_THEME. */
  private loadTheme(): NotdTheme {
    if (!isPlatformBrowser(this.platformId)) {
      return DEFAULT_THEME;
    }

    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch (e) {
      console.warn('ThemeService: Could not read from localStorage', e);
    }

    return DEFAULT_THEME;
  }

  /** Write the theme to localStorage. */
  private saveTheme(theme: NotdTheme): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (e) {
      console.warn('ThemeService: Could not write to localStorage', e);
    }
  }

  /**
   * Apply the theme to the DOM by setting `data-theme` attribute on <html>.
   * This allows CSS to target themes via `html[data-theme="dark"] { ... }`.
   * Also emits the new value on currentTheme$.
   */
  private applyTheme(theme: NotdTheme): void {
    this.currentTheme$.next(theme);

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const html = this.document.documentElement;
    if (html) {
      this.renderer.setAttribute(html, 'data-theme', theme);
    }
  }
}
